import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { validateResponse } from "@/api/validateResponse";
import {
  BookingSlotListResponseSchema,
  ResourceListResponseSchema,
  type BookingSlot,
  type Resource,
} from "@/api/types/booking";
import {
  PartyActivityCatalogItemSchema,
  PartyAddonTypeSchema,
  type PartyActivityCatalogItem,
  type PartyFormat,
  type PartyOccasion,
} from "@/api/types/party";
import { z } from "zod";

/**
 * Dedicated axios instances for the PUBLIC party-inquiry surface — same
 * rationale as useClassRegistrationPublic.ts's own client: these are
 * completely separate, unauthenticated backend routers (no bearer token,
 * no cookie, no /login redirect on a 4xx). Resource/slot metadata is
 * fetched from the EXISTING public booking-availability router
 * (app/api/public_bookings.py) — the party-inquiry backend deliberately
 * does not duplicate that metadata (see app/api/public_parties.py's
 * module docstring) — filtered client-side to event-room resources.
 */
const publicBookingsClient = axios.create({
  baseURL: "/api/public/bookings",
  headers: { "Content-Type": "application/json" },
});

const publicPartiesClient = axios.create({
  baseURL: "/api/public/parties",
  headers: { "Content-Type": "application/json" },
});

export function usePublicEventRooms() {
  return useQuery<Resource[]>({
    queryKey: ["public-parties", "resources"],
    queryFn: async () => {
      const { data } = await publicBookingsClient.get("/resources");
      const items = validateResponse(ResourceListResponseSchema, data, "GET /public/bookings/resources").items;
      return items.filter((r) => r.kind === "event_room");
    },
    staleTime: 60 * 1000,
  });
}

export function usePublicEventRoomSlots() {
  return useQuery<BookingSlot[]>({
    queryKey: ["public-parties", "slots"],
    queryFn: async () => {
      const { data } = await publicBookingsClient.get("/slots?kind=event_room");
      return validateResponse(BookingSlotListResponseSchema, data, "GET /public/bookings/slots").items;
    },
    staleTime: 60 * 1000,
  });
}

/** Zone A — the public party activity catalog (GET /api/public/parties/activities),
 * backing PartyInquiryPage.tsx's optional format/selection picker. */
export function usePublicPartyActivities(format?: PartyFormat) {
  return useQuery<PartyActivityCatalogItem[]>({
    queryKey: ["public-parties", "activities", format ?? "all"],
    queryFn: async () => {
      const { data } = await publicPartiesClient.get(format ? `/activities?format=${format}` : "/activities");
      return validateResponse(z.array(PartyActivityCatalogItemSchema), data, "GET /public/parties/activities");
    },
    staleTime: 5 * 60 * 1000,
  });
}

const PublicPartyInquiryResponseSchema = z.object({
  ok: z.boolean(),
  suggested_addons: z.array(PartyAddonTypeSchema),
});
export type PublicPartyInquiryResponse = z.infer<typeof PublicPartyInquiryResponseSchema>;

export interface PublicPartyInquiryInput {
  resource_id: string;
  slot_id: string;
  booking_date: string;
  extra_hour: boolean;
  name: string;
  email?: string;
  phone?: string;
  headcount?: number | null;
  occasion: PartyOccasion;
  child_age?: number | null;
  notes?: string;
  email_consent: boolean;
  sms_consent: boolean;
  website: string; // honeypot — always submitted empty by a real guardian
  // Zone A — OPTIONAL here (unlike the admin create form): a bare
  // "checking availability" inquiry shouldn't have to commit to a plan
  // yet. If format is given, selections must match its exact required
  // count (see PARTY_SELECTION_COUNT_BY_FORMAT).
  format?: PartyFormat;
  selections?: string[];
}

export function useSubmitPartyInquiry() {
  const queryClient = useQueryClient();
  return useMutation<PublicPartyInquiryResponse, unknown, PublicPartyInquiryInput>({
    mutationFn: async (input) => {
      const { data } = await publicPartiesClient.post("/inquiries", input);
      return validateResponse(PublicPartyInquiryResponseSchema, data, "POST /public/parties/inquiries");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["public-parties"] });
    },
  });
}
